import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import {
  TrendingUp, AlertTriangle, Flame, Users, ShieldAlert, ShieldX, Activity, Loader2,
} from "lucide-react";

const zoneStyle = {
  healthy: "bg-emerald-100 text-emerald-700",
  at_risk: "bg-amber-100 text-amber-700",
  dead: "bg-red-100 text-red-700",
};
const zoneLabel = { healthy: "Healthy", at_risk: "At Risk", dead: "Dead" };

const KpiTile = ({ icon: Icon, label, value, tone = "slate", testid }) => (
  <div className={`rounded-xl border bg-white p-4 ${tone === 'red' ? 'border-red-200' : tone === 'emerald' ? 'border-emerald-200' : tone === 'amber' ? 'border-amber-200' : 'border-slate-200'}`} data-testid={testid}>
    <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500">
      <Icon className={`w-4 h-4 ${tone === 'red' ? 'text-red-600' : tone === 'emerald' ? 'text-emerald-600' : tone === 'amber' ? 'text-amber-600' : 'text-slate-600'}`} />
      {label}
    </div>
    <div className={`mt-1 text-2xl font-bold ${tone === 'red' ? 'text-red-600' : tone === 'emerald' ? 'text-emerald-600' : tone === 'amber' ? 'text-amber-600' : 'text-slate-900'}`}>
      {value}
    </div>
  </div>
);

export default function BdManagerReview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ zone: "all", hot_only: false, red_flag: "" });

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.zone !== "all") params.append("zone", filters.zone);
      if (filters.hot_only) params.append("hot_only", "true");
      if (filters.red_flag) params.append("red_flag", filters.red_flag);
      const res = await axios.get(`${API}/bd-manager/summary?${params.toString()}`);
      setData(res.data);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to load BD review");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }
  if (!data) return null;
  const { kpis, scorecard, customers, computed_at } = data;

  return (
    <div className="p-6 space-y-6" data-testid="bd-manager-review-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            BD Manager Weekly Review
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Bangladesh customer health, hot lead progress, agent scorecard.
            <span className="ml-2 text-xs">As of {new Date(computed_at).toLocaleString()}</span>
          </p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiTile icon={Users} label="Total Customers" value={kpis.total_customers} testid="kpi-total" />
        <KpiTile icon={Activity} label="Healthy" value={kpis.healthy} tone="emerald" testid="kpi-healthy" />
        <KpiTile icon={AlertTriangle} label="At Risk" value={kpis.at_risk} tone="amber" testid="kpi-at-risk" />
        <KpiTile icon={ShieldX} label="Dead" value={kpis.dead} tone="red" testid="kpi-dead" />
        <KpiTile icon={Flame} label="Hot Leads" value={kpis.hot_leads} tone="amber" testid="kpi-hot" />
        <KpiTile icon={ShieldAlert} label="Pending Screenshots" value={kpis.pending_verifications} tone="red" testid="kpi-pending" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiTile icon={Flame} label="Hot Leads Stuck 14d+" value={kpis.stuck_hot_14d} tone="red" testid="kpi-stuck-hot" />
        <KpiTile icon={AlertTriangle} label="At Risk 14d+" value={kpis.stuck_at_risk_14d} tone="amber" testid="kpi-stuck-risk" />
        <KpiTile icon={ShieldX} label="Zeroed Calls" value={kpis.zeroed_calls} tone="slate" testid="kpi-zeroed" />
      </div>

      {/* Red Flag chips (Phase 4) */}
      {kpis.red_flags && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            Red Flag Signals (click to filter)
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { code: "no_call_14d", label: "No call 14d+" },
              { code: "all_short_30d", label: "All calls < 1min (30d)" },
              { code: "hot_stuck_21d", label: "Hot stuck 21d+" },
              { code: "dead_inactive_14d", label: "Dead + inactive 14d+" },
            ].map(rf => {
              const n = kpis.red_flags[rf.code] || 0;
              const active = filters.red_flag === rf.code;
              return (
                <button
                  key={rf.code}
                  type="button"
                  onClick={() => setFilters(f => ({ ...f, red_flag: active ? "" : rf.code }))}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    active
                      ? "bg-red-600 text-white border-red-600"
                      : n > 0
                        ? "bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
                        : "bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed"
                  }`}
                  disabled={!active && n === 0}
                  data-testid={`flag-chip-${rf.code}`}
                >
                  {rf.label} <span className="font-bold">· {n}</span>
                </button>
              );
            })}
            {filters.red_flag && (
              <button
                type="button"
                onClick={() => setFilters(f => ({ ...f, red_flag: "" }))}
                className="text-xs px-3 py-1.5 rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50"
                data-testid="flag-chip-clear"
              >
                Clear filter ✕
              </button>
            )}
          </div>
        </div>
      )}

      {/* Scorecard */}
      <Card data-testid="scorecard-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            Agent Scorecard
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-left">
                <tr>
                  <th className="px-3 py-2 font-semibold">Agent</th>
                  <th className="px-3 py-2 font-semibold text-right">Customers</th>
                  <th className="px-3 py-2 font-semibold text-right">Avg Health</th>
                  <th className="px-3 py-2 font-semibold text-right">Healthy</th>
                  <th className="px-3 py-2 font-semibold text-right">At Risk</th>
                  <th className="px-3 py-2 font-semibold text-right">Dead</th>
                  <th className="px-3 py-2 font-semibold text-right">Hot</th>
                  <th className="px-3 py-2 font-semibold text-right">Stuck Hot</th>
                  <th className="px-3 py-2 font-semibold text-right">🚩 Pending</th>
                </tr>
              </thead>
              <tbody>
                {scorecard.map((s) => (
                  <tr key={s.agent_id || 'unassigned'} className="border-t border-slate-100" data-testid={`agent-row-${s.agent_id || 'unassigned'}`}>
                    <td className="px-3 py-2 font-medium text-slate-800">{s.agent_name}</td>
                    <td className="px-3 py-2 text-right">{s.total_customers}</td>
                    <td className="px-3 py-2 text-right">
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                        s.avg_health >= 42 ? 'bg-emerald-100 text-emerald-700' :
                        s.avg_health >= 24 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                      }`}>{s.avg_health}/60</span>
                    </td>
                    <td className="px-3 py-2 text-right text-emerald-700">{s.healthy}</td>
                    <td className="px-3 py-2 text-right text-amber-700">{s.at_risk}</td>
                    <td className="px-3 py-2 text-right text-red-700">{s.dead}</td>
                    <td className="px-3 py-2 text-right">{s.hot_leads}</td>
                    <td className={`px-3 py-2 text-right ${s.stuck_hot > 0 ? 'text-red-700 font-bold' : 'text-slate-400'}`}>{s.stuck_hot}</td>
                    <td className={`px-3 py-2 text-right ${s.pending_verifications > 0 ? 'text-red-700 font-bold' : 'text-slate-400'}`}>{s.pending_verifications}</td>
                  </tr>
                ))}
                {scorecard.length === 0 && (
                  <tr><td colSpan={9} className="px-3 py-4 text-center text-slate-400">No BD customers assigned yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Customer Summary */}
      <Card data-testid="customer-summary-card">
        <CardHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Customer Summary
              <span className="text-xs font-normal text-slate-500">({customers.length} shown)</span>
            </CardTitle>
            <div className="flex items-center gap-2">
              <Select value={filters.zone} onValueChange={(v) => setFilters(f => ({ ...f, zone: v }))}>
                <SelectTrigger className="h-9 w-40" data-testid="filter-zone"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Zones</SelectItem>
                  <SelectItem value="healthy">Healthy</SelectItem>
                  <SelectItem value="at_risk">At Risk</SelectItem>
                  <SelectItem value="dead">Dead</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant={filters.hot_only ? "default" : "outline"}
                size="sm"
                onClick={() => setFilters(f => ({ ...f, hot_only: !f.hot_only }))}
                className={filters.hot_only ? "bg-amber-500 hover:bg-amber-600 text-white" : ""}
                data-testid="filter-hot-only"
              >
                <Flame className="w-4 h-4 mr-1" />
                {filters.hot_only ? "Hot Only" : "Hot Only"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-left">
                <tr>
                  <th className="px-3 py-2 font-semibold">Customer</th>
                  <th className="px-3 py-2 font-semibold">Agent</th>
                  <th className="px-3 py-2 font-semibold text-right">Avg Health</th>
                  <th className="px-3 py-2 font-semibold text-right">Worst</th>
                  <th className="px-3 py-2 font-semibold">Zone</th>
                  <th className="px-3 py-2 font-semibold">Hot Stage</th>
                  <th className="px-3 py-2 font-semibold">Flags</th>
                  <th className="px-3 py-2 font-semibold">Last Change</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((c) => (
                  <tr key={c.lead_id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`customer-row-${c.lead_id}`}>
                    <td className="px-3 py-2">
                      <Link to={`/leads/${c.lead_id}`} className="text-blue-600 hover:underline font-medium">
                        {c.company_name}
                      </Link>
                      <div className="text-[10px] text-slate-400">{c.requirements_count} requirement{c.requirements_count === 1 ? '' : 's'}</div>
                    </td>
                    <td className="px-3 py-2 text-slate-700">{c.assigned_to_name || <span className="text-slate-400">Unassigned</span>}</td>
                    <td className="px-3 py-2 text-right font-bold">{c.avg_health}/60</td>
                    <td className="px-3 py-2 text-right text-slate-500">{c.worst_health}</td>
                    <td className="px-3 py-2">
                      <Badge className={zoneStyle[c.zone]}>{zoneLabel[c.zone]}</Badge>
                    </td>
                    <td className="px-3 py-2">
                      {c.hot_stage ? (
                        <span className="text-xs font-semibold text-amber-700 capitalize">{c.hot_stage.replace(/_/g, ' ')}</span>
                      ) : (
                        <span className="text-xs text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {c.stuck_14d && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold">14d+ stuck</span>
                        )}
                        {c.pending_verifications > 0 && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold">🚩 {c.pending_verifications}</span>
                        )}
                        {(c.red_flags || []).map(rf => (
                          <span
                            key={rf.code}
                            className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                              rf.severity === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                            }`}
                            title={rf.label}
                            data-testid={`row-flag-${c.lead_id}-${rf.code}`}
                          >
                            {rf.label}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {c.last_change_at ? new Date(c.last_change_at).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
                {customers.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-400">No customers match the current filters</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
